import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Snowflake, Mail, LogOut, RefreshCw } from "lucide-react";

/**
 * Full-page block screen rendered when the manager's company subscription is frozen.
 * Reachable by axios 402 redirect (see lib/api.js) or by direct navigation to /frozen.
 */
export default function Frozen() {
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = () => {
    setChecking(true);
    api.get("/subscription/status")
      .then(({ data }) => {
        setInfo(data);
        if (data.status !== "frozen") {
          // Status restored — bounce them back to the main app.
          nav("/calendar", { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const logout = () => {
    localStorage.removeItem("jb_token");
    nav("/login", { replace: true });
  };

  const contact = info?.platform_contact_email || "info@jobsbook.app";
  const expires = info?.expires_at ? new Date(info.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-slate-950 text-slate-100" data-testid="frozen-page">
      <div className="max-w-2xl w-full bg-slate-900 border border-rose-800/40 rounded-[10px] p-10 shadow-2xl">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-14 w-14 rounded-full bg-rose-600/15 border border-rose-700/40 grid place-items-center">
            <Snowflake className="h-7 w-7 text-rose-300" />
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-300">// Account frozen</div>
            <h1 className="text-3xl font-black tracking-tight">Your JobsBook account is frozen</h1>
          </div>
        </div>

        <p className="text-slate-300 leading-relaxed">
          {expires
            ? <>Your subscription expired on <span className="font-bold text-slate-100">{expires}</span> and the 10-day grace period has ended.</>
            : <>Your subscription is currently frozen.</>}
          {" "}All bookings, customers, and invoices are safely retained &mdash; nothing has been deleted &mdash; but the app is paused until your subscription is renewed.
        </p>

        <div className="mt-6 bg-slate-950/60 border border-slate-800 rounded-[8px] p-5">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1">Contact platform admin</div>
          <a href={`mailto:${contact}`} className="inline-flex items-center gap-2 text-rose-300 hover:text-rose-200 text-lg font-bold mono break-all"
             data-testid="frozen-contact-email">
            <Mail className="h-5 w-5" /> {contact}
          </a>
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          <Button onClick={load} disabled={checking} variant="outline"
                  className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200"
                  data-testid="frozen-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${checking ? "animate-spin" : ""}`} /> Re-check status
          </Button>
          <Button onClick={logout} variant="outline"
                  className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200"
                  data-testid="frozen-logout">
            <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
          </Button>
          <Link to="/login" className="text-slate-500 self-center text-xs mono uppercase tracking-wider hover:text-slate-300 ml-1">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
